import * as React from 'react';

import { sendDownloadInitiateMessage } from '~showbie/messages/sendDownloadMessage';

import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { IconButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog/Dialog';
import {
  IconCopy,
  IconDocument,
  IconDownload,
  IconLock,
  IconShare,
} from '~shared/components/icons/standard';
import { MenuDivider, StyledMenuItem } from '~shared/components/Menu';
import { TooltipV2 } from '~shared/components/TooltipV2';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import {
  PaywallDialogGeneric,
  PaywallDispatch,
} from '~teacher/components/PaywallDialog';
import { QuizListMenuList } from '~teacher/components/quizzes/QuizListItem/QuizListMenuList';
import { ShareDialogDispatch } from '~teacher/components/ShareQuizDialog/types';
import { useQuizLimit } from '~teacher/hook';
import { useWorkspaceList } from '~teacher/hook/workspaces';
import { CopyQuizDialog } from '~teacher/quizzes/CopyQuizDialog/CopyQuizDialog';
import { proCheck } from '~teacher/util/proCheck';

import { downloadEmptyQuiz } from '../../../../api/quiz/downloadQuiz';
import { Quiz } from '../../../../api/quiz/getQuizzes/types';
import { useFeatureSupportCheck } from '../../../../showbie/hooks/useFeatureSupportCheck';
import { useLibraryQuizActions, useTrashQuizActions } from './ActionProviders';
import { QuizAnswerKeyExportModal } from './QuizAnswerKeyExportModal';
import { QuizListItem, QuizListItemBaseProps } from './QuizListItem';

export type QuizProps = QuizListItemBaseProps &
  Quiz & {
    paywallDispatch: PaywallDispatch;
    shareDialogDispatch?: ShareDialogDispatch;
  };

function useCopyDialog(paywallDispatch: PaywallDispatch) {
  const quizLimit = useQuizLimit();
  const [isCopyDialogOpen, setIsCopyDialogOpen] = React.useState(false);

  const toggleCopyDialog = () => {
    if (!quizLimit.isFetched) return;

    if (quizLimit.data.currentQuizCount >= quizLimit.data.limit) {
      paywallDispatch({
        type: 'OPEN',
        touch: [
          PaywallScreens.QuizListLimitReached,
          PaywallScreens.QuizListCopyQuiz,
        ],
      });
      return;
    }

    setIsCopyDialogOpen(!isCopyDialogOpen);
  };

  return { isCopyDialogOpen, toggleCopyDialog };
}

export function QuizListQuiz({
  id,
  uniqueId,
  name,
  soc,
  parentId,
  deleted,
  paywallDispatch,
  shareDialogDispatch,
  disableHover,
  sharable,
  locked,
  ...props
}: QuizProps) {
  const { isCopyDialogOpen, toggleCopyDialog } = useCopyDialog(paywallDispatch);
  const [isAnswerKeyDialogOpen, setIsAnswerKeyDialogOpen] =
    React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isAnswerKeyPaywallOpen, setIsAnswerKeyPaywallOpen] =
    React.useState(false);

  // Hide download and answer key export on unsupported assessment versions
  const { canDownloadAndExportQuiz } = useFeatureSupportCheck();
  const appContext = useTeacherApplicationContext();

  const handleExportAnswerKeyClick = React.useCallback(() => {
    if (proCheck.hasPaidPlan()) {
      setIsAnswerKeyDialogOpen(true);
    } else {
      setIsAnswerKeyPaywallOpen(true);
    }
  }, []);

  const handleCloseAnswerKeyDialog = React.useCallback(() => {
    setIsAnswerKeyDialogOpen(false);
    // Reset exporting state when dialog closes to allow reopening
    setIsExporting(false);
  }, []);
  const openShareDialog = React.useCallback(() => {
    shareDialogDispatch({
      type: 'Open',
      payload: {
        soc: soc,
        quizId: id,
        isSharingEnabled: sharable,
        name: name,
      },
    });
  }, [soc, id, sharable, name, shareDialogDispatch]);
  const [isDownloadDisabled, setIsDownloadDisabled] = React.useState(false);
  const handleDownloadClick = () => {
    setIsDownloadDisabled(true);
    setTimeout(() => setIsDownloadDisabled(false), 1000);

    downloadEmptyQuiz(id, appContext, (downloadUrl: string) => {
      // For Showbie Assessments, send Download.Initiate PostMessage
      if (appContext === TeacherApplicationContext.ShowbieAssessments) {
        sendDownloadInitiateMessage(downloadUrl);
      }
    });
  };

  const IconComponent = locked ? IconLock : IconDocument;

  return (
    <QuizListItem
      {...props}
      locked={locked}
      paywallDispatch={paywallDispatch}
      uniqueId={uniqueId}
      disableHover={disableHover}
      itemIcon={
        <IconComponent
          className="flex text-blue-900 ml-1"
          title={translate('Quiz')}
          titleId={`icon-title-${uniqueId}`}
        />
      }
      href={deleted ? undefined : `#edit-quiz/${soc}`}
      shortcutActions={
        deleted || locked
          ? null
          : getLibraryShortcuts({
              uniqueId: uniqueId,
              toggleShareDialog: openShareDialog,
              toggleCopyDialog: toggleCopyDialog,
              isDownloadDisabled: isDownloadDisabled,
              onDownloadClicked: handleDownloadClick,
              canDownloadAndExportQuiz,
            })
      }
      actionsMenu={
        deleted ? (
          <TrashActionsMenu uniqueId={uniqueId} />
        ) : (
          <LibraryActionsMenu
            uniqueId={uniqueId}
            openSharingDialog={openShareDialog}
            toggleCopyDialog={toggleCopyDialog}
            onDownloadClicked={handleDownloadClick}
            onExportAnswerKeyClicked={handleExportAnswerKeyClick}
            disabled={locked}
            canDownloadAndExportQuiz={canDownloadAndExportQuiz}
          />
        )
      }
    >
      {name}
      <CopyQuizDialog
        isOpen={isCopyDialogOpen}
        title={`${translate('Duplicate Quiz')}`}
        originalName={name}
        parentId={parentId}
        socNumber={soc}
        closeDialog={toggleCopyDialog}
        closeLabel={`${translate('Cancel')}`}
        paywallDispatch={paywallDispatch}
      />
      {/*
      If the user is a pro user, this will show the answer key export modal.
      If the user is not a pro user, this will show a generic paywall dialog.
      */}
      <Dialog
        id="answer-key-export-dialog"
        open={isAnswerKeyDialogOpen}
        onClose={handleCloseAnswerKeyDialog}
        size="default"
        title={translate('Export Answer Key')}
      >
        {isAnswerKeyDialogOpen && (
          <QuizAnswerKeyExportModal
            key={`answer-key-${id}-${isAnswerKeyDialogOpen}`}
            quizId={id}
            onExportingChange={setIsExporting}
          />
        )}
      </Dialog>
      <PaywallDialogGeneric
        open={isAnswerKeyPaywallOpen}
        onClose={() => setIsAnswerKeyPaywallOpen(false)}
        paywallScreen={PaywallScreens.PersonalLibraryDownloadAnswerKey}
      />
    </QuizListItem>
  );
}

/** @todo Replace `deleteQuiz` with actual shortcut actions. */
interface LibraryShortcutsProps {
  uniqueId: string;
  toggleShareDialog: () => void;
  toggleCopyDialog: () => void;
  isDownloadDisabled: boolean;
  onDownloadClicked: () => void;
  canDownloadAndExportQuiz: boolean;
}

function getLibraryShortcuts({
  uniqueId,
  toggleShareDialog,
  toggleCopyDialog,
  isDownloadDisabled = false,
  onDownloadClicked,
  canDownloadAndExportQuiz,
}: LibraryShortcutsProps) {
  const shortcuts = [
    <TooltipV2 key={`tooltip-share-${uniqueId}`} label={translate('Share')}>
      <IconButton
        key={`share-${uniqueId}`}
        aria-label={translate('Share')}
        onClick={toggleShareDialog}
        tabIndex={-1}
      >
        <IconShare />
      </IconButton>
    </TooltipV2>,
    <TooltipV2
      key={`tooltip-duplicate-${uniqueId}`}
      label={translate('Duplicate')}
    >
      <IconButton
        key={`duplicate-${uniqueId}`}
        aria-label={translate('Duplicate')}
        onClick={toggleCopyDialog}
        tabIndex={-1}
      >
        <IconCopy />
      </IconButton>
    </TooltipV2>,
  ];

  if (canDownloadAndExportQuiz) {
    shortcuts.push(
      <TooltipV2
        key={`tooltip-download-${uniqueId}`}
        label={translate('Download')}
      >
        <IconButton
          key={`download-${uniqueId}`}
          aria-label={translate('Download')}
          disabled={isDownloadDisabled}
          onClick={onDownloadClicked}
          tabIndex={-1}
        >
          <IconDownload />
        </IconButton>
      </TooltipV2>
    );
  }

  return shortcuts;
}

interface LibraryActionsProps {
  uniqueId: string;
  openSharingDialog: () => void;
  toggleCopyDialog: () => void;
  onDownloadClicked: () => void;
  onExportAnswerKeyClicked: () => void;
  disabled: boolean;
  canDownloadAndExportQuiz: boolean;
}

function LibraryActionsMenu({
  uniqueId,
  openSharingDialog,
  toggleCopyDialog,
  onDownloadClicked,
  onExportAnswerKeyClicked,
  disabled,
  canDownloadAndExportQuiz,
}: LibraryActionsProps) {
  const { deleteQuiz, openMoveDialog, openCopyDialog } =
    useLibraryQuizActions();

  const { data: hasSharedLibraries } = useWorkspaceList({
    select: (workspaces) => workspaces.length > 0,
  });
  const onDeleteClick = React.useCallback(
    () => deleteQuiz(uniqueId),
    [uniqueId, deleteQuiz]
  );
  const onMoveClick = React.useCallback(
    () => openMoveDialog(uniqueId),
    [uniqueId, openMoveDialog]
  );

  const onCopyToWorkspaceClick = React.useCallback(
    () => openCopyDialog(uniqueId),
    [uniqueId, openCopyDialog]
  );

  return (
    <QuizListMenuList>
      <StyledMenuItem disabled={disabled} onClick={openSharingDialog}>
        {translate('Share')}
      </StyledMenuItem>
      <StyledMenuItem disabled={disabled} onClick={toggleCopyDialog}>
        {translate('Duplicate')}
      </StyledMenuItem>
      <StyledMenuItem
        disabled={disabled || !hasSharedLibraries}
        onClick={onCopyToWorkspaceClick}
      >
        {translate('Copy To Shared Library')}
      </StyledMenuItem>
      <StyledMenuItem disabled={disabled} onClick={onMoveClick}>
        {translate('Move to…')}
      </StyledMenuItem>

      {canDownloadAndExportQuiz && (
        <>
          <StyledMenuItem disabled={disabled} onClick={onDownloadClicked}>
            {translate('Download')}
          </StyledMenuItem>
          <StyledMenuItem
            disabled={disabled}
            onClick={onExportAnswerKeyClicked}
          >
            {translate('Export Answer Key')}
            {proCheck.hasPaidPlan() ? (
              ''
            ) : (
              <div className="flex items-center pl-2">
                <Badge>Pro</Badge>
              </div>
            )}
          </StyledMenuItem>
        </>
      )}

      <MenuDivider />
      <StyledMenuItem onClick={onDeleteClick} isDestructive={true}>
        {translate('Delete')}
      </StyledMenuItem>
    </QuizListMenuList>
  );
}

interface TrashActionsProps {
  uniqueId: string;
}

function TrashActionsMenu({ uniqueId }: TrashActionsProps) {
  const { restoreQuiz, purgeQuiz } = useTrashQuizActions();
  return (
    <QuizListMenuList>
      <StyledMenuItem onClick={() => restoreQuiz(uniqueId)}>
        {translate('Restore')}
      </StyledMenuItem>
      <StyledMenuItem onClick={() => purgeQuiz(uniqueId)}>
        {translate('Delete Forever')}
      </StyledMenuItem>
    </QuizListMenuList>
  );
}
